import React, { useState } from 'react';
import {
  TextField,
  Box,
  IconButton,
  Collapse,
  Typography,
  Chip
} from '@mui/material';
import {
  NoteAdd as NoteAddIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

/**
 * Private Notes Component
 * 
 * Allows assessors to add private internal notes to assessment responses.
 * These notes are only visible in reports if explicitly included.
 * 
 * Usage:
 * <PrivateNotesField
 *   assessmentId={assessmentId}
 *   responseId={responseId}
 *   initialNotes={existingNotes}
 *   onSave={(notes) => console.log('Saved:', notes)}
 * />
 */
const PrivateNotesField = ({ 
  assessmentId, 
  responseId, 
  initialNotes = '',
  onSave 
}) => {
  const [expanded, setExpanded] = useState(!!initialNotes);
  const [notes, setNotes] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleNotesChange = (event) => {
    setNotes(event.target.value);
    setHasChanges(event.target.value !== initialNotes);
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      await api.put(
        `/assessments/${assessmentId}/responses/${responseId}/private-notes`,
        { private_notes: notes }
      );
      
      toast.success('Private notes saved');
      setHasChanges(false);
      
      if (onSave) {
        onSave(notes);
      }
    } catch (error) {
      console.error('Failed to save private notes:', error);
      toast.error('Failed to save private notes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(initialNotes);
    setHasChanges(false);
    if (!initialNotes) {
      setExpanded(false);
    }
  };

  const handleToggle = () => {
    setExpanded(!expanded);
    if (!expanded && !notes) {
      // Auto-focus the text field when expanding
      setTimeout(() => {
        document.getElementById(`private-notes-${responseId}`)?.focus();
      }, 100);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      {/* Header with toggle button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton
          size="small"
          onClick={handleToggle}
          sx={{ 
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
        
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          Private Assessor Notes
        </Typography>
        
        {notes && !expanded && (
          <Chip 
            label="Has notes" 
            size="small" 
            color="primary"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
        
        {!expanded && !notes && (
          <Chip 
            icon={<NoteAddIcon />}
            label="Add notes" 
            size="small" 
            variant="outlined"
            onClick={handleToggle}
            sx={{ ml: 1, cursor: 'pointer' }}
          />
        )}
      </Box>

      {/* Expandable notes field */}
      <Collapse in={expanded}>
        <Box sx={{ 
          bgcolor: '#FFF9E6', 
          border: '1px solid #FFE082',
          borderRadius: 1,
          p: 2
        }}>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
            <strong>Internal use only:</strong> These notes are for assessor reference and will only appear in reports if explicitly included in PDF options.
          </Typography>
          
          <TextField
            id={`private-notes-${responseId}`}
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={handleNotesChange}
            placeholder="Add internal notes, observations, or follow-up items..."
            variant="outlined"
            size="small"
            sx={{ 
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem'
              }
            }}
          />
          
          {/* Action buttons */}
          {hasChanges && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <IconButton
                size="small"
                onClick={handleCancel}
                disabled={saving}
                title="Cancel changes"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="primary"
                onClick={handleSave}
                disabled={saving}
                title="Save private notes"
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          
          {saving && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
              Saving...
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PrivateNotesField;
